import { LEGAL_PAGES, LEGAL_PAGE_IDS, type LegalPageId } from './legal/pages.ts';
import { DEFAULT_LOCALE, LOCALES, type Locale } from './locales.ts';

/**
 * ĐÂY LÀ TRUNG TÂM CỦA PHẦN SEO.
 *
 * Mỗi trang có MỘT id nội bộ và một slug RIÊNG cho từng ngôn ngữ — URL tiếng Việt phải là
 * tiếng Việt (một trang `tinh-nang` sau này là `/vi/tinh-nang`, không phải slug tiếng Anh dịch
 * nửa vời). Router, hreflang, sitemap, breadcrumb và menu đều sinh ra từ bảng này, nên không
 * thể có chuyện sitemap liệt kê một URL mà router không biết, hoặc hreflang trỏ vào trang 404.
 *
 * Hiện chỉ có MỘT trang: landing này là một trang dài 13 section, nav trỏ anchor trong trang —
 * xem `docs/design/2026-08-18-nexa-landing-completion-design.md` §3 và §4.
 *
 * Vẫn để dạng union (không thu về `string`): thêm lại một trang là thêm 1 phần tử vào `PAGES` +
 * 1 component ở `apps/web/src/pages`, đúng như contract §1.1 mô tả — không phải sửa sitemap,
 * hreflang hay nginx. Thu về `string` là mất khả năng để TypeScript chỉ ra mọi chỗ còn thiếu.
 */
export type PageId = 'home' | LegalPageId;

/** Tần suất thay đổi khai trong sitemap.xml. */
export type ChangeFreq = 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';

export interface PageRoute {
  readonly id: PageId;
  /** Slug theo từng locale. Chuỗi rỗng = trang gốc của locale đó (`/vi/`). */
  readonly slug: Record<Locale, string>;
  /** Độ ưu tiên trong sitemap (0..1). Trang chủ 1.0, trang phụ thấp hơn. */
  readonly priority: number;
  readonly changefreq: ChangeFreq;
  /** false ⇒ không đưa vào sitemap và gắn noindex (ví dụ trang cảm ơn, landing quảng cáo). */
  readonly indexable?: boolean;
}

/**
 * 7 trang pháp lý sinh RA TỪ `LEGAL_PAGES`, không gõ lại ở đây.
 *
 * Gõ lại là khai cùng một slug ở hai chỗ — đúng thứ §1.1 cấm, và là chỗ để hai bảng trôi khỏi
 * nhau trong im lặng. `priority`/`changefreq` khai ở đây vì chúng thuộc SEO của bảng route, không
 * thuộc bản chất của văn bản: bản lưu trữ không vào sitemap nên `priority` của nó không có tác
 * dụng, nhưng kiểu vẫn đòi nên giữ giá trị thấp nhất cho khớp ngữ nghĩa.
 */
const LEGAL_ROUTES: readonly PageRoute[] = LEGAL_PAGE_IDS.map((id) => {
  const def = LEGAL_PAGES[id];
  return {
    id,
    slug: { vi: def.slug, en: def.slug },
    priority: def.indexable ? 0.3 : 0.1,
    changefreq: 'yearly' as const,
    indexable: def.indexable,
  };
});

export const PAGES: readonly PageRoute[] = [
  {
    id: 'home',
    slug: { vi: '', en: '' },
    priority: 1.0,
    changefreq: 'weekly',
  },
  ...LEGAL_ROUTES,
];

const PAGE_BY_ID = new Map<PageId, PageRoute>(PAGES.map((page) => [page.id, page]));

export function getPage(id: PageId): PageRoute {
  const page = PAGE_BY_ID.get(id);
  if (!page) throw new Error(`[content] Không có trang nào mang id "${id}" trong PAGES.`);
  return page;
}

/**
 * Slug trần của một trang trong một ngôn ngữ — không locale, không query.
 *
 * Cây route dùng hàm này. Tách khỏi `pathFor` vì từ khi ngôn ngữ nằm ở query, cây route KHÔNG
 * còn biết tới locale nữa: `/terms/` là một route duy nhất phục vụ mọi ngôn ngữ, và ngôn ngữ
 * do bản build quyết định (xem `docs/design/2026-08-27-lang-query-param-design.md` §3.1).
 */
export function routeSlugFor(id: PageId, locale: Locale): string {
  return getPage(id).slug[locale];
}

/**
 * URL ĐỐI NGOẠI của một trang — thứ người dùng thấy trên thanh địa chỉ, thứ đi vào canonical,
 * hreflang, sitemap và mọi liên kết nội bộ.
 *
 * Ngôn ngữ mặc định ra URL TRẦN (`/terms/`); ngôn ngữ khác mang `?lang=` (`/terms/?lang=en`).
 * Bất đối xứng này là CHỦ Ý: nó giữ URL của bản mặc định ngắn nhất và không có URL trùng nội
 * dung — `?lang=<mặc định>` bị nginx 301 về bản trần.
 *
 * Dấu `/` cuối LUÔN đứng TRƯỚC `?`. Đặt sau (`/terms?lang=en`) là để nginx phải redirect thêm
 * một nhịp, và mỗi redirect là một vòng request mà crawler phải trả.
 *
 * ĐỪNG dùng hàm này để tìm file trong `dist` — dùng `distPathFor`.
 */
export function pathFor(id: PageId, locale: Locale): string {
  const slug = getPage(id).slug[locale];
  const base = slug === '' ? '/' : `/${slug}/`;
  return locale === DEFAULT_LOCALE ? base : `${base}?lang=${locale}`;
}

/**
 * Đường dẫn FILE của một trang trong `dist` — KHÔNG phải URL người dùng thấy.
 *
 * Bản mặc định prerender ra gốc (`dist/terms/index.html`); bản còn lại vào
 * `dist/__lang/<locale>/terms/index.html`. nginx map `?lang=xx` sang đúng cây `__lang/xx` mà
 * KHÔNG khai tên locale nào — nó chỉ hỏi "thư mục đó có thật không". Nên `__lang` không phải
 * chi tiết cài đặt tuỳ tiện: đổi tên nó là đổi hợp đồng với `site.conf`.
 *
 * Ba cổng đọc `dist` (`check-seo-output`, `hydrate-probe`, `check-legal-text`) phải dùng hàm
 * này, không phải `pathFor` — `pathFor` giờ trả chuỗi có query, không phải tên thư mục.
 */
export function distPathFor(id: PageId, locale: Locale): string {
  const slug = getPage(id).slug[locale];
  const prefix = locale === DEFAULT_LOCALE ? '' : `/__lang/${locale}`;
  return slug === '' ? `${prefix}/` : `${prefix}/${slug}/`;
}

/**
 * URL của hình dạng CŨ — prefix locale (`/vi/privacy/`, `/en/`).
 *
 * Đây KHÔNG phải URL còn sống: nginx 301 nó sang `pathFor()`. Nó tồn tại vì hai chỗ vẫn cần gọi
 * tên nó được:
 *
 *   1. **Văn bản pháp lý đã đăng ký** viết cứng 4 href theo hình dạng cũ, và không sửa được —
 *      bản bằng chứng ở `packages/content/legal-evidence/` neo bằng số byte, digest, VÀ phép so
 *      nguyên văn với commit gốc, nên sửa văn bản là phá chính cơ chế chứng minh nó không trôi.
 *      Cổng `check:legal` cần biết 4 href đó là URL legacy hợp lệ, không phải link chết.
 *   2. **nginx** 301 chúng — `location ~ ^/([a-z]{2})/(.*)$` (xem `site.conf`).
 *
 * Sinh BẰNG MÁY từ cùng bảng route, không phải một danh sách gõ tay: xoá một trang là legacy của
 * nó biến mất theo, nên tập này không thể trở thành nơi trú của URL đã chết — đúng mối lo §1.1.
 */
export function legacyPathFor(id: PageId, locale: Locale): string {
  const slug = getPage(id).slug[locale];
  return slug === '' ? `/${locale}/` : `/${locale}/${slug}/`;
}

export interface ResolvedPath {
  readonly id: PageId;
  readonly locale: Locale;
  /** URL đối ngoại — `pathFor`. Dùng cho canonical/hreflang/sitemap/liên kết. */
  readonly path: string;
  /** Đường dẫn file trong `dist` — `distPathFor`. Dùng cho mọi cổng đọc bản build. */
  readonly distPath: string;
  /** URL hình dạng CŨ — `legacyPathFor`. nginx 301 nó sang `path`. */
  readonly legacyPath: string;
  readonly page: PageRoute;
}

/** Toàn bộ (trang × ngôn ngữ) — router prerender và sitemap đều duyệt danh sách này. */
export function allPaths(): readonly ResolvedPath[] {
  return PAGES.flatMap((page) =>
    LOCALES.map((locale) => ({
      id: page.id,
      locale,
      path: pathFor(page.id, locale),
      distPath: distPathFor(page.id, locale),
      legacyPath: legacyPathFor(page.id, locale),
      page,
    })),
  );
}

/** Các bản dịch của cùng một trang — dùng để sinh hreflang và bộ chuyển ngôn ngữ. */
export function alternatesFor(id: PageId): Record<Locale, string> {
  return Object.fromEntries(LOCALES.map((locale) => [locale, pathFor(id, locale)])) as Record<
    Locale,
    string
  >;
}
